import {
  Box,
  Button,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { setAutoFreeze } from "immer";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";

import apiHelper from "../../../../util/ApiHelper";
import ImageUpload from "../../../ImageUpload";
import { useLoading } from "../../../../util/LoadingProvider";
import editorConfiguration from "../../../../util/EditorConfiguration";
import { modal } from "../../../../util";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

const frontendUrl = process.env.REACT_APP_CINEFAN_WEB_HOSTNAME;
setAutoFreeze(false);

const useStyles = makeStyles((theme) => ({
  tabpanel: {
    background: "#FFFFFF",
  },
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  imageGallery: {
    border: "1px solid #CCCCCC",
    minHeight: "160px",
    borderRadius: "3px",
    position: "relative",
  },
  imageGalleryControl: {
    position: "relative",
    width: "100%",
    display: "flex",
    height: "40px",
    bottom: "0",
    borderTop: "1px solid #CCCCCC",
    padding: "5px",
  },
  imageGalleryBlk: {
    minHeight: "120px",
    padding: "5px",
  },
  imgPreview: {
    maxWidth: "200px",
    margin: "10px",
    display: "inline-flex",
    position: "relative",
    verticalAlign: "top",
    "& img": {
      width: "100%",
      height: "auto",
    },
    "& button": {
      position: "absolute",
      top: "-10px",
      right: "-10px",
      zIndex: "10",
      display: "block",
      color: "rgba(48, 111, 157, 0.8)",
      background: "white",
      padding: 0,
      transition: "200ms all ease-out",
      "&:hover": {
        color: "rgba(48, 111, 157, 1)",
        background: "white",
      },
    },
  },
  permalink: {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#333333",
    padding: "0 5px",
    "& .link": {
      color: "#306F9D",
      margin: "0 10px",
      display: "flex",
      alignItems: "center",
      "& input": {
        padding: "7px 14px",
        fontSize: "13px",
      },
    },
    "& button": {
      padding: "2px 20px",
      textTransform: "inherit",
      color: "#727272",
    },
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

const defaultFormValues = {
  jobTitleTc: "",
  jobTitleEn: "",
  type: 1,
  descTc: "",
  descEn: "",
  jobDutiesTc: "",
  jobDutiesEn: "",
  jobRequirementsTc: "",
  jobRequirementsEn: "",
  status: 1,
};

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

function EditPositionPage({ disabled, ...props }) {
  const { match, history } = props;
  let { id } = useParams();
  const classes = useStyles();
  const [activeTab, setActiveTab] = React.useState(0);
  const [defaultValues, setDefaultValues] = React.useState(defaultFormValues);
  const typeList = { "1": "Permanent", "2": "Temporary" };
  const statusList = { "1": "Enable", "2": "Disable" };

  const { setLoading } = useLoading();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
  });

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getPosition = React.useCallback(async () => {
    if(id != "new") {
      try {
        setLoading(true);
        const result = await apiHelper.hkiff.getJobById(id);
        setDefaultValues(result.data);
        reset(result.data);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  const deletePosition = async () => {
    const decision = await modal.fire({
      title: "Delete Position",
      text: "Are you sure to delete this position?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteJobById(id);
        toast.dark("Position deleted.");
        history.push("/hkiff/job-opportunity/position");
      } catch (err) {
        console.log(err);
      }
    }
  };

  const onSubmit = () => async (value) => {
    if(id != "new") {
      // update 
      try {
        setLoading(true);
        console.log(value);
        const result = await apiHelper.hkiff.updateJobById(id, value);
        if (result.data.id) {
          toast.dark("Position updated.");
          getPosition();
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }else {
      // create 
      try {
        setLoading(true);
        console.log(value);
        const result = await apiHelper.hkiff.postJob(value);
        if (result.data.id) {
          toast.dark("Position created.");
          history.push("/hkiff/job-opportunity/position/"+result.data.id);
          getPosition();
        }
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    }
    
  };

  React.useEffect(() => {
    getPosition();
  }, []);

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
      </Typography>
      <Tabs
        value={activeTab}
        onChange={handleChange}
        aria-label="nav tabs example"
        TabIndicatorProps={{ style: { top: "0", background: "#306F9D" } }}
      >
        <Tab
          label="English"
          {...a11yProps(0)}
        />
        <Tab
          label="繁體中文"
          {...a11yProps(1)}
        />
      </Tabs>
      <form>
        <fieldset disabled={disabled} className="fieldset">
          <TabPanel value={activeTab} index={0}>
            <Box className={classes.innerBox} style={{ marginBottom: 0 }}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.jobTitleEn}
                    name="jobTitleEn"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Job Title"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        error={!!error}
                        helperText={error ? error.message : "The name is how it appears on your site."}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.type}
                    name="type"
                    control={control}
                    render={({
                      field: { value, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <ToggleButtonGroup
                        {...rest}
                        value={getValues(`type`)}
                        onChange={(event, newFormats) => {
                          if (newFormats) {
                            setValue(`type`, newFormats);
                          }
                        }}
                        exclusive
                        aria-label="status"
                        style={{ width: "350px", background: "#FFFFFF" }}
                      >
                        <ToggleButton
                          value={1}
                          aria-label={typeList[1]}
                          disabled={disabled}
                        >
                          {typeList[1]}
                        </ToggleButton>
                        <ToggleButton
                          value={2}
                          aria-label={typeList[2]}
                          disabled={disabled}
                        >
                          {typeList[2]}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    fontWeight={900}
                    fontSize={13}
                    mb={2}
                    mt={2}
                    width="100%"
                  >
                    <Typography variant="p">Description</Typography>
                  </Box>
                  <Controller
                    name="descEn"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>


                <Grid item xs={12}>
                  <Box
                    fontWeight={900}
                    fontSize={13}
                    mb={2}
                    style={{ marginTop: "30px" }}
                    width="100%"
                  >
                    <Typography variant="p">Job Duty</Typography>
                  </Box>
                  <Controller
                    name="jobDutiesEn"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    fontWeight={900}
                    fontSize={13}
                    mb={2}
                    style={{ marginTop: "30px" }}
                    width="100%"
                  >
                    <Typography variant="p">Job Requirement</Typography>
                  </Box>
                  <Controller
                    name="jobRequirementsEn"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.status}
                    name="status"
                    control={control}
                    render={({
                      field: { value, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <ToggleButtonGroup
                        {...rest}
                        value={getValues(`status`)}
                        onChange={(event, newFormats) => {
                          if (newFormats) {
                            setValue(`status`, newFormats);
                          }
                        }}
                        exclusive
                        aria-label="status"
                        style={{ width: "350px", marginBottom: "40px", background: "#FFFFFF" }}
                      >
                        <ToggleButton
                          value={1}
                          aria-label={statusList[1]}
                          disabled={disabled}
                        >
                          {statusList[1]}
                        </ToggleButton>
                        <ToggleButton
                          value={2}
                          aria-label={statusList[2]}
                          disabled={disabled}
                        >
                          {statusList[2]}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Grid>

              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <Box className={classes.innerBox} style={{ marginBottom: 0 }}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.jobTitleTc}
                    name="jobTitleTc"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Job Title"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        error={!!error}
                        helperText={error ? error.message : "The name is how it appears on your site."}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.type}
                    name="type"
                    control={control}
                    render={({
                      field: { value, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <ToggleButtonGroup
                        {...rest}
                        value={getValues(`type`)}
                        onChange={(event, newFormats) => {
                          if (newFormats) {
                            setValue(`type`, newFormats);
                          }
                        }}
                        exclusive
                        aria-label="status"
                        style={{ width: "350px", background: "#FFFFFF" }}
                      >
                        <ToggleButton
                          value={1}
                          aria-label={typeList[1]}
                          disabled={disabled}
                        >
                          {typeList[1]}
                        </ToggleButton>
                        <ToggleButton
                          value={2}
                          aria-label={typeList[2]}
                          disabled={disabled}
                        >
                          {typeList[2]}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    fontWeight={900}
                    fontSize={13}
                    mb={2}
                    mt={2}
                    width="100%"
                  >
                    <Typography variant="p">Description</Typography>
                  </Box>
                  <Controller
                    name="descTc"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>


                <Grid item xs={12}>
                  <Box
                    fontWeight={900}
                    fontSize={13}
                    mb={2}
                    style={{ marginTop: "30px" }}
                    width="100%"
                  >
                    <Typography variant="p">Job Duty</Typography>
                  </Box>
                  <Controller
                    name="jobDutiesTc"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    fontWeight={900}
                    fontSize={13}
                    mb={2}
                    style={{ marginTop: "30px" }}
                    width="100%"
                  >
                    <Typography variant="p">Job Requirement</Typography>
                  </Box>
                  <Controller
                    name="jobRequirementsTc"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    defaultValue={defaultValues.status}
                    name="status"
                    control={control}
                    render={({
                      field: { value, onChange, ...rest },
                      fieldState: { error },
                    }) => (
                      <ToggleButtonGroup
                        {...rest}
                        value={getValues(`status`)}
                        onChange={(event, newFormats) => {
                          if (newFormats) {
                            setValue(`status`, newFormats);
                          }
                        }}
                        exclusive
                        aria-label="status"
                        style={{ width: "350px", marginBottom: "40px", background: "#FFFFFF" }}
                      >
                        <ToggleButton
                          value={1}
                          aria-label={statusList[1]}
                          disabled={disabled}
                        >
                          {statusList[1]}
                        </ToggleButton>
                        <ToggleButton
                          value={2}
                          aria-label={statusList[2]}
                          disabled={disabled}
                        >
                          {statusList[2]}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  />
                </Grid>

              </Grid>
            </Box>
          </TabPanel>
          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
                onClick={handleSubmit(onSubmit("PUBLISHED"))}
              >
                Publish
              </Button>
              {id != "new" &&
                <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#962013",
                  padding: "9px 16px",
                }}
                onClick={() => deletePosition()}
              >
                Delete
              </Button>
              }
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}

export default EditPositionPage;
